import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { ClipboardList, Pencil, Plus, Trash2 } from 'lucide-react';

import type { BusinessProcessResponse } from '@/domain/models/project.js';
import { useApp } from '@/presentation/hooks/use-app.js';

interface Props {
    processes: BusinessProcessResponse[];
    onAdd: () => void;
    onEdit: (process: BusinessProcessResponse) => void;
    onToggleActive: (process: BusinessProcessResponse) => void;
    onDelete: (processId: string) => void;
}

export function ProcessesTab({ processes, onAdd, onEdit, onToggleActive, onDelete }: Props) {
    const { translate } = useApp();

    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                    {translate('projects.businessProcesses')}
                </Typography>
                <Button variant="outlined" size="small" startIcon={<Plus size={14} />} onClick={onAdd} sx={{ textTransform: 'none' }}>
                    {translate('projects.addBusinessProcess')}
                </Button>
            </Stack>
            {processes.length === 0 ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 6, textAlign: 'center' }}>
                    <ClipboardList size={32} style={{ color: '#D1D5DB', marginBottom: 12 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {translate('projects.noProcesses')}
                    </Typography>
                    <Button variant="outlined" size="small" startIcon={<Plus size={14} />} onClick={onAdd} sx={{ textTransform: 'none' }}>
                        {translate('projects.addBusinessProcess')}
                    </Button>
                </Box>
            ) : (
                <Card variant="outlined" sx={{ borderRadius: 2, borderColor: 'divider', boxShadow: 1, overflow: 'hidden' }}>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: 'action.hover' }}>
                                    <TableCell sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                                        {translate('projects.processName')}
                                    </TableCell>
                                    <TableCell sx={{ fontSize: 11, fontWeight: 600, color: 'text.secondary', textTransform: 'uppercase' }}>
                                        {translate('projects.processActive')}
                                    </TableCell>
                                    <TableCell sx={{ width: 80 }} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {processes.map((process) => (
                                    <TableRow key={process.id} hover>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {process.name}
                                            </Typography>
                                            {process.description && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {process.description}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Switch size="small" checked={process.isActive} onChange={() => onToggleActive(process)} />
                                        </TableCell>
                                        <TableCell>
                                            <Stack direction="row" spacing={0.5}>
                                                <IconButton size="small" onClick={() => onEdit(process)}>
                                                    <Pencil size={14} />
                                                </IconButton>
                                                <IconButton size="small" color="error" onClick={() => onDelete(process.id)}>
                                                    <Trash2 size={14} />
                                                </IconButton>
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Card>
            )}
        </Box>
    );
}
